/**
 * Email Filter — deterministic order scoring (no AI).
 * Port of mittens filterOrders.ts + retailers.ts.
 * Scores emails by retailer domain, subject keywords, body patterns.
 */

// ── Retailer Domains ──

export const RETAILER_DOMAINS = {
  // Fashion marketplaces
  'depop.com': 'Depop',
  'poshmark.com': 'Poshmark',
  'thredup.com': 'ThredUp',
  'grailed.com': 'Grailed',
  'vestiairecollective.com': 'Vestiaire Collective',
  'therealreal.com': 'The RealReal',
  'mercari.com': 'Mercari',

  // Fast fashion / retail
  'asos.com': 'ASOS',
  'zara.com': 'Zara',
  'hm.com': 'H&M',
  'uniqlo.com': 'Uniqlo',
  'urbanoutfitters.com': 'Urban Outfitters',
  'freepeople.com': 'Free People',
  'anthropologie.com': 'Anthropologie',
  'forever21.com': 'Forever 21',
  'gap.com': 'Gap',
  'oldnavy.com': 'Old Navy',
  'abercrombie.com': 'Abercrombie & Fitch',
  'shein.com': 'SHEIN',
  'revolve.com': 'Revolve',
  'nastygal.com': 'Nasty Gal',
  'prettylittlething.com': 'PrettyLittleThing',
  'boohoo.com': 'Boohoo',

  // Sportswear
  'nike.com': 'Nike',
  'adidas.com': 'Adidas',
  'lululemon.com': 'Lululemon',
  'newbalance.com': 'New Balance',
  'puma.com': 'Puma',
  'reebok.com': 'Reebok',

  // Department / luxury
  'nordstrom.com': 'Nordstrom',
  'macys.com': "Macy's",
  'bloomingdales.com': "Bloomingdale's",
  'net-a-porter.com': 'Net-a-Porter',
  'ssense.com': 'SSENSE',
  'farfetch.com': 'Farfetch',
  'mytheresa.com': 'Mytheresa',
  'saks.com': 'Saks Fifth Avenue',
  'neimanmarcus.com': 'Neiman Marcus',

  // General marketplaces
  'amazon.com': 'Amazon',
  'ebay.com': 'eBay',
  'etsy.com': 'Etsy',
  'walmart.com': 'Walmart',
  'target.com': 'Target',
  'shopify.com': 'Shopify',

  // Shoes
  'goat.com': 'GOAT',
  'stockx.com': 'StockX',
  'dsw.com': 'DSW',
  'zappos.com': 'Zappos',

  // Accessories / beauty
  'glossier.com': 'Glossier',
  'sephora.com': 'Sephora',
  'ulta.com': 'Ulta',
  'warbyparker.com': 'Warby Parker'
};

const ORDER_SUBJECT_KEYWORDS = [
  'order confirmation',
  'order confirmed',
  'your order',
  'order receipt',
  'order #',
  'receipt for',
  'shipping confirmation',
  'has shipped',
  'order shipped',
  'your shipment',
  'delivery confirmation',
  'out for delivery',
  'delivered',
  'purchase confirmation',
  'payment receipt',
  'invoice',
  'thank you for your order',
  'thank you for your purchase'
];

const ORDER_BODY_PATTERNS = {
  price: /(?:\$|USD|EUR|GBP|£|€)\s*\d+(?:\.\d{2})?|\d+(?:\.\d{2})?\s*(?:USD|EUR|GBP)/gi,
  orderNumber: /(?:order\s*(?:#|number|no\.?)?|confirmation\s*(?:#|number)?|ref(?:erence)?)\s*[:#]?\s*([A-Z0-9\-]{4,20})/gi,
  tracking: /(?:tracking|track)\s*(?:#|number|no\.?)?\s*[:#]?\s*([A-Z0-9]{10,30})/gi
};

/**
 * Check if a sender email matches a known retailer.
 */
export function matchRetailer(senderEmail) {
  const emailLower = senderEmail.toLowerCase();
  for (const [domain, name] of Object.entries(RETAILER_DOMAINS)) {
    if (emailLower.includes(domain)) return name;
  }
  return null;
}

/**
 * Score a single email for order relevance (0-1).
 */
function scoreEmail(candidate) {
  let score = 0;
  const maxScore = 5;

  const subjectLower = candidate.subject.toLowerCase();
  const snippetLower = candidate.snippet.toLowerCase();
  const bodyLower = (candidate.cleanedBody || '').toLowerCase();
  const searchText = `${subjectLower} ${snippetLower} ${bodyLower}`;

  // Known retailer sender (+2)
  if (matchRetailer(candidate.from)) score += 2;

  // Subject contains order keywords (+1.5)
  if (ORDER_SUBJECT_KEYWORDS.some(kw => subjectLower.includes(kw))) score += 1.5;

  // Body contains price patterns (+0.5)
  if (ORDER_BODY_PATTERNS.price.test(searchText)) score += 0.5;
  ORDER_BODY_PATTERNS.price.lastIndex = 0;

  // Body contains order number (+0.5)
  if (ORDER_BODY_PATTERNS.orderNumber.test(searchText)) score += 0.5;
  ORDER_BODY_PATTERNS.orderNumber.lastIndex = 0;

  // Body contains tracking info (+0.5)
  if (ORDER_BODY_PATTERNS.tracking.test(searchText)) score += 0.5;
  ORDER_BODY_PATTERNS.tracking.lastIndex = 0;

  return Math.min(score / maxScore, 1);
}

/**
 * Score and filter emails for order confirmation relevance.
 * Returns only candidates above threshold, sorted by score descending.
 */
export function filterOrderEmails(candidates) {
  const SCORE_THRESHOLD = 0.3;

  const scored = candidates.map(c => ({
    ...c,
    score: scoreEmail(c),
    retailer: matchRetailer(c.from) || undefined
  }));

  return scored
    .filter(c => c.score >= SCORE_THRESHOLD)
    .sort((a, b) => b.score - a.score);
}

/**
 * Build a default order search query for Gmail.
 * Used as the initial scan query.
 * @param {object} dateRange - Optional { after: 'YYYY-MM-DD', before: 'YYYY-MM-DD' }
 */
export function buildDefaultOrderQuery(dateRange) {
  const subjectFilters = `(subject:"order confirmation" OR subject:"order shipped" OR subject:"your order" OR subject:"shipping confirmation" OR subject:"purchase confirmation" OR subject:"receipt for" OR subject:"order receipt")`;

  // If dateRange is provided (user chose a range) but has no 'after', it means "all time"
  if (dateRange && !dateRange.after && !dateRange.before) {
    console.log('[emailFilter] buildDefaultOrderQuery: ALL TIME, no date filter');
    return subjectFilters;
  }

  let query = subjectFilters;

  if (dateRange?.after) {
    query += ` after:${dateRange.after.replace(/-/g, '/')}`;
  } else {
    // No dateRange at all — default to last 30 days
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    const after = thirtyDaysAgo.toISOString().split('T')[0].replace(/-/g, '/');
    query += ` after:${after}`;
  }

  if (dateRange?.before) {
    query += ` before:${dateRange.before.replace(/-/g, '/')}`;
  }

  console.log('[emailFilter] buildDefaultOrderQuery:', query);
  return query;
}

